/**
 * /login — identity sign-in (email + password).
 *
 * Mirrors `frontend/src/app/(auth)/login/page.tsx` (the Next.js
 * original). Three post-submit branches:
 *   1. `select_workspace:true` — the identity has >=2 active
 *      memberships. The backend has set an identity-only session
 *      cookie. We open the picker; selecting a workspace POSTs
 *      /auth/activate.
 *   2. `totp_required:true` — TOTP already enrolled; the access cookie
 *      is totp=false. Route to /2fa-verify before any TOTP-gated page.
 *   3. M-1 `totp_enrollment_required` 403 — a mandatory-2FA role
 *      (super_admin / hr_manager) hasn't enrolled. The backend set an
 *      enrolment-scoped identity-only session; force /2fa-setup.
 *   4. Otherwise — single-membership / platform identity. Honour
 *      `?next=` (sanitised via safeNext) or fall back to /dashboard.
 *
 * Passkeys (spec §6.2 A): "Sign in with passkey" (hidden when the browser
 * cannot) and the email field's autofill (conditional UI, started on mount,
 * aborted on unmount / password submit) sign in with no password. The
 * result is the same union as a password login and routes through the
 * same branches above; a dismissed browser sheet is silent.
 *
 * Migration note: 1:1 port of the Next.js page. Replacements:
 *   - next/link → @tanstack/react-router <Link to=…>
 *   - useRouter().replace() → useNavigate({ to, replace: true })
 *   - useSearchParams() → Route.useSearch() (typed, Zod-validated)
 *   - <Suspense> wrapper (Next.js client-component pattern) removed
 *     — Vite has no SSR boundary, so the inner form renders directly.
 */

import { useEffect, useRef, useState } from "react";
import {
  Link,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, Loader2 } from "lucide-react";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth, safeNext } from "@/lib/auth";
import { mandatoryEnrolmentRoute, sessionLanding, type AuthRoute } from "@/lib/auth/auth-gate";
import { loginSchema, type LoginValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { toApiError, isTotpEnrollmentRequired, refusalNeedsPasswordChange } from "@/lib/api";
import {
  isSelectWorkspace,
  type LoginResponse,
  type LoginResult,
  type TwoFactorMethodFlags,
  type WorkspaceChoice,
} from "@/types/auth";
import {
  cancelPasskeyCeremony,
  isPasskeyCancelled,
  passkeyAutofillSupported,
  autofillRestartDelayMs,
  passkeyErrorPhase,
  passkeyLoginErrorAction,
  secondFactorPending,
  passkeySupported,
} from "@/lib/auth/passkey";
import { WorkspacePickerDialog } from "@/components/auth/workspace-picker-dialog";

const FIELDS = ["email", "password"] as const;

// Search-param schema. TanStack Router will validate every nav into
// /login against this — invalid shapes are stripped, not coerced into
// runtime bugs. `next` is still passed through safeNext() at use sites
// (defence in depth: we never trust the URL as the SOLE guard).
const loginSearchSchema = z.object({
  next: z.string().optional().catch(undefined),
  reason: z.enum(["session_revoked"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/login")({
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { next: nextRaw, reason } = Route.useSearch();
  const { login, loginWithPasskey, activate, refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  // Passkey sign-in: the button shows only when the browser can run a
  // WebAuthn ceremony; autofill (conditional UI) only when it can offer
  // passkeys from the email field.
  const [canPasskey, setCanPasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const autofillOK = useRef(false);
  const mounted = useRef(false);
  // Which autofill run is current: bumped whenever this page aborts it (a
  // password submit, the button ceremony), so only a run that ended on its
  // own (the browser's timeout) restarts. The budget stops a browser that
  // rejects conditional requests at once from looping.
  const autofillRun = useRef(0);
  const autofillRestarts = useRef(0);
  const autofillRefusals = useRef(0);
  const autofillTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set once this page navigates away to the next sign-in step: no
  // autofill ceremony may start after that.
  const navigated = useRef(false);

  // Multi-company picker: populated when /auth/login returned
  // select_workspace:true. The backend already set an IDENTITY-ONLY
  // session cookie; picking a workspace POSTs to /auth/activate.
  const [picker, setPicker] = useState<{
    workspaces: WorkspaceChoice[];
    /** Set while the sign-in still owes a second factor. */
    methods: TwoFactorMethodFlags | null;
  } | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  // safeNext() rejects off-origin / protocol-relative targets so a
  // crafted ?next=https://evil… cannot bounce the user off-site.
  const nextPath = safeNext(nextRaw);

  // The api client redirects here with ?reason=session_revoked after
  // the backend reported refresh_reuse (token theft → all sessions
  // revoked). Surface a neutral notice — no specifics leaked.
  const sessionRevoked = reason === "session_revoked";
  const revokedToastShown = useRef(false);
  useEffect(() => {
    if (sessionRevoked && !revokedToastShown.current) {
      revokedToastShown.current = true;
      toast.error(
        "Your sessions were ended for security. Please sign in again.",
      );
    }
  }, [sessionRevoked]);

  function go(route: AuthRoute) {
    navigated.current = true;
    stopAutofill();
    void navigate({ to: route.to, search: route.search as never, replace: true });
  }

  // M-1 mandatory-2FA gate: 2FA is mandatory for this identity (role or
  // company policy) and it has no method. The backend already 403'd and
  // set an enrolment-scoped identity-only session. Round 2 T-H1: a
  // temporary password is changed FIRST (the refusal body and /me carry
  // must_change_password), then /2fa-setup (required mode).
  async function routeToMandatory2fa(err: unknown) {
    navigated.current = true;
    stopAutofill();
    // Hydrate /me: login() threw before it could, and the next pages read
    // the session (the forced-password copy, the setup reason).
    const me = await refresh();
    go(mandatoryEnrolmentRoute(refusalNeedsPasswordChange(err) || me?.must_change_password === true, nextPath));
  }

  async function routeAfterSession(res: LoginResponse) {
    // Pending 2FA (password accepted, second factor owed): only that body
    // carries `methods`. totp_required alone is true for every 2FA user —
    // also after a passkey sign-in, which already completed 2FA.
    if (secondFactorPending(res)) {
      go({ to: "/2fa-verify", search: { next: nextPath } });
      return;
    }
    // Re-fetch /me so we have the freshly-activated tenant's slug —
    // useAuth.login() already called refresh() internally, but its
    // setState hasn't propagated to this closure yet. Calling refresh
    // again is cheap (one /me round trip) and gives us the slug
    // synchronously via its return value. sessionLanding also routes a
    // temporary password to /change-password first.
    const me = await refresh();
    go(sessionLanding(me, nextPath));
  }

  // One router for every sign-in result (password or passkey).
  async function routeLoginResult(res: LoginResult) {
    // >=2 memberships → identity-only session was set; open the
    // picker. /auth/activate completes the pick with no re-auth.
    if (isSelectWorkspace(res)) {
      setPicker({
        workspaces: res.workspaces,
        methods: secondFactorPending(res) ? (res.methods ?? null) : null,
      });
      return;
    }
    // Single membership / platform identity → full session.
    await routeAfterSession(res);
  }

  function clearAutofillTimer() {
    if (autofillTimer.current) clearTimeout(autofillTimer.current);
    autofillTimer.current = null;
  }

  // Stops the waiting autofill ceremony (it resolves as cancelled, quietly).
  function stopAutofill() {
    autofillRun.current++;
    clearAutofillTimer();
    cancelPasskeyCeremony();
  }

  // Passkey sign-in failures. A dismissed sheet never gets here
  // (PASSKEY_CANCELLED).
  function onPasskeyError(err: unknown, fromAutofill: boolean) {
    if (navigated.current || !mounted.current) return;
    const apiErr = toApiError(err);
    // lib/auth/passkey passkeyLoginErrorAction: a failed autofill START
    // stays quiet; anything after the person picked a passkey (locked,
    // inactive account, server error, network — round 2 F-I2) is shown,
    // and autofill is offered again.
    const action = passkeyLoginErrorAction({
      code: apiErr.error,
      message: apiErr.message,
      phase: passkeyErrorPhase(err),
      fromAutofill,
    });
    if (action.kind === "enrol") {
      void routeToMandatory2fa(err);
      return;
    }
    if (action.kind === "quiet") return;
    toast.error(action.message);
    // Back off before offering autofill again: an authenticator that answers
    // by itself must not be able to hammer the login endpoint.
    autofillRefusals.current++;
    clearAutofillTimer();
    autofillTimer.current = setTimeout(startAutofill, autofillRestartDelayMs(autofillRefusals.current));
  }

  // Conditional UI: waits for the user to pick a passkey from the email
  // field's autofill list. Only one ceremony runs at a time — starting the
  // button ceremony (or a password submit) aborts this one.
  function startAutofill() {
    if (!autofillOK.current || !mounted.current || navigated.current) return;
    if (autofillRestarts.current >= 10) return;
    autofillRestarts.current++;
    const run = ++autofillRun.current;
    void loginWithPasskey({
      autofill: true,
      // The begin request may still be in flight when the button ceremony
      // starts, a password is submitted or the page moves on: then the
      // browser ceremony must not start (it would abort the button's).
      shouldContinue: () => run === autofillRun.current && mounted.current && !navigated.current,
    })
      .then(async (res) => {
        if (isPasskeyCancelled(res)) {
          // Ended by the browser (its timeout), not by this page → re-arm.
          if (run === autofillRun.current) startAutofill();
          return;
        }
        await routeLoginResult(res);
      })
      .catch((err) => onPasskeyError(err, true));
  }

  useEffect(() => {
    mounted.current = true;
    let active = true;
    void (async () => {
      const [supported, autofill] = await Promise.all([
        passkeySupported(),
        passkeyAutofillSupported(),
      ]);
      if (!active) return;
      setCanPasskey(supported);
      autofillOK.current = autofill;
      startAutofill();
    })();
    return () => {
      active = false;
      mounted.current = false;
      stopAutofill();
    };
    // startAutofill reads refs + stable auth callbacks; run once on mount.
  }, []);

  async function onPasskeyClick() {
    setPasskeyBusy(true);
    try {
      // Starting this ceremony aborts the autofill one — and a pending
      // back-off restart must not fire mid-ceremony and cancel it.
      clearAutofillTimer();
      autofillRun.current++;
      const res = await loginWithPasskey({
        shouldContinue: () => mounted.current && !navigated.current,
      });
      if (isPasskeyCancelled(res)) {
        startAutofill();
        return;
      }
      await routeLoginResult(res);
    } catch (err) {
      onPasskeyError(err, false);
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function onSubmit(values: LoginValues) {
    setSubmitting(true);
    // A password sign-in replaces any waiting passkey autofill.
    stopAutofill();
    let signedIn = false;
    try {
      const res = await login({
        email: values.email,
        password: values.password,
      });
      signedIn = true;
      await routeLoginResult(res);
    } catch (err) {
      // M-1: 403 totp_enrollment_required — mandatory 2FA, not a
      // credential failure. The backend set the enrolment-scoped
      // session; force enrolment instead of showing a form error.
      if (isTotpEnrollmentRequired(err)) {
        await routeToMandatory2fa(err);
        return;
      }
      handleAuthError(err, form.setError, FIELDS);
    } finally {
      setSubmitting(false);
      // startAutofill is a no-op once this page navigated on.
      if (!signedIn) startAutofill();
    }
  }

  // Picker → POST /auth/activate { slug }. On success the backend
  // rotates in the full tenant session; route through the SAME
  // post-session path (honours totp_required).
  async function onPickWorkspace(slug: string) {
    if (!picker) return;
    try {
      const res = await activate(slug);
      setPicker(null);
      await routeAfterSession(res);
    } catch (err) {
      if (isTotpEnrollmentRequired(err)) {
        setPicker(null);
        await routeToMandatory2fa(err);
        return;
      }
      toast.error(toApiError(err).message);
      throw err;
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Sign In</CardTitle>
          <CardDescription>
            Welcome back. Enter your credentials to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionRevoked && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              Your sessions were ended for security. Please sign in
              again.
            </div>
          )}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="username webauthn"
                        placeholder="you@company.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        placeholder="••••••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    {/* Forgot link AFTER the input so the keyboard
                        tab flow is Email → Password → Sign in →
                        Forgot. Visually right-aligned under the input
                        keeps it discoverable. */}
                    <div className="flex justify-end">
                      <Link
                        to="/forgot"
                        className="inline-flex items-center text-xs font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
              >
                {submitting && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Sign In
              </Button>
            </form>
          </Form>
          {canPasskey && (
            <>
              <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                or
                <span className="h-px flex-1 bg-border" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={onPasskeyClick}
                disabled={passkeyBusy || submitting}
                data-testid="passkey-sign-in"
              >
                {passkeyBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                Sign In with Passkey
              </Button>
            </>
          )}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            No account?{" "}
            <Link
              to="/register"
              className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
            >
              Create a workspace
            </Link>
          </p>
        </CardContent>
      </Card>

      <WorkspacePickerDialog
        open={picker !== null}
        workspaces={picker?.workspaces ?? []}
        pendingMethods={picker?.methods ?? null}
        onCancel={() => setPicker(null)}
        onSelect={onPickWorkspace}
      />
    </>
  );
}
